import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LuTrash2 } from "react-icons/lu";
import { useParams } from "react-router-dom";

import { DATE_FORMATS, DATES } from "../../app/commonOps/CommonDateOps";
import Button from "../../components/button/Button";
import { OutlineButton } from "../../components/button/OutlineButton";
import { CompactDatePicker } from "../../components/datepicker/datepicker";
import InputTime from "../../components/inputs/inputTime";
import { SettingsConfirmModal } from "../../components/modal/SettingsConfirmModal";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import {
  deleteTime,
  getAllTimes,
  getTimesByWork,
} from "../../services/AppService";
import {
  endTimer,
  setTimer,
  setTimerDuration,
  startTimer,
} from "../../services/TimerService";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useLazyGetTimesByWorkQuery } from "../../slices/WorkSlice";
import { workTasksSlice } from "../../slices/WorkTasksSlice";
import { transformDateToUTC } from "../../utils/transformDate";
import { BillableSwitch } from "../time/BillableSwitch";
import { getTimerTargetType } from "./timer/timerTarget";

interface IAddTimerProps {
  isOpen: boolean;
  closeModal: () => void;
}

type ConfirmTimeForm = {
  date: Date;
};

const READONLY_INPUT_CLASS =
  "input input-bordered flex h-8 w-full items-center border-[#D4D6D9] bg-gray-100 text-sm font-medium disabled:border-[#D4D6D9] disabled:bg-gray-100";

const FIELD_LABEL_CLASS = "label-text mb-2 font-medium text-[#1D2C3B]";
const ACTION_BUTTON_CLASS =
  "h-8 min-h-0 !w-[100px] px-0 py-0 text-sm font-medium";

const AddTimer: React.FC<IAddTimerProps> = ({ isOpen, closeModal }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.appReducer);
  const { workId } = useParams();

  const { timer, duration } = useAppSelector((state) => state.timerReducer);
  const [getTime] = useLazyGetTimesByWorkQuery();
  const timerTargetType = getTimerTargetType(timer);
  const isWorkTaskTimer = timerTargetType === "workTask";
  const timerTypeLabel = isWorkTaskTimer ? "Subtask" : "Work";
  const taskName = isWorkTaskTimer
    ? timer?.workTask?.name || "N/A"
    : timer?.work?.name || "N/A";
  const customerName = timer?.customerName?.trim() || "N/A";

  const [time, setTime] = useState({
    h: (moment.duration(duration, "seconds").hours() || 0).toString(),
    m: (moment.duration(duration, "seconds").minutes() || 0).toString(),
  });
  const [notes, setNotes] = useState("");
  const [isBillable, setIsBillable] = useState(timer?.isBillable ?? true);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { handleSubmit, control } = useForm<ConfirmTimeForm>({
    defaultValues: {
      date: DATES.today(),
    },
  });

  const onSubmit = async (data: ConfirmTimeForm) => {
    try {
      const timerPayload = {
        id: timer?.id || "",
        date: moment(data.date).utc(),
        duration: Number(time.h) * 3600 + Number(time.m) * 60,
        workId: timer?.workId || null,
        notes: notes || "",
        assignedUserId: timer?.assignedUserId || null,
        workTypeId: timer?.workTypeId || null,
        targetType: timerTargetType,
        jobTaskId: timer?.jobTaskId || null,
        isBillable,
      };

      await dispatch(endTimer(timerPayload)).unwrap();
      dispatch(workTasksSlice.util.invalidateTags(["WorkTask"]));
      dispatch(setTimer(null));
      dispatch(setTimerDuration(0));
    } catch (error) {
      console.error("Failed to confirm timer", error);
    }
    dispatch(getAllTimes({ id: getLinkedFirm()?.orgId, userId: user.id }));
    dispatch(
      getTimesByWork({
        jobId: `${timer?.workId}`,
        orgId: `${getLinkedFirm()?.orgId}`,
      }),
    );
    if (workId) {
      getTime({ workId, orgId: getLinkedFirm()?.orgId || "" });
    }
    closeModal();
  };
  useEffect(() => {
    if (isOpen) {
      setTime({
        h: (moment.duration(duration, "seconds").hours() || 0).toString(),
        m: (moment.duration(duration, "seconds").minutes() || 0).toString(),
      });
    }
  }, [duration, isOpen]);
  useEffect(() => {
    if (isOpen) setIsBillable(timer?.isBillable ?? true);
  }, [isOpen, timer?.isBillable]);

  const handleDelete = () => {
    dispatch(setTimer(null));
    dispatch(setTimerDuration(0));
    if (timer?.id) {
      dispatch(deleteTime(timer));
    }
    closeModal();
  };

  return (
    <>
      <div
        onClick={closeModal}
        id={"edit-time"}
        className={
          "fixed left-0 top-0 z-[4] flex h-screen w-screen items-center justify-center bg-gray-500/20"
        }>
        <div
          onClick={(event) => event.stopPropagation()}
          className={
            "max-h-[750px] w-full max-w-lg overflow-hidden rounded-[10px] bg-white font-Manrope"
          }>
          <div
            className={
              "grid grid-cols-[1fr_max-content] border-b border-gray-300 px-6 py-2 text-center text-base font-semibold"
            }>
            <div className={"flex items-center justify-start text-[16px]"}>
              Confirm Time
            </div>
            <button
              type={"button"}
              className={"close-popup cursor-pointer text-end"}
              onClick={closeModal}>
              <img src={"/img/closse.png"} alt={""} />
            </button>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={
              "max-h-[calc(750px-41px)] w-full overflow-auto px-6 pb-6 pt-4"
            }>
            <div>
              <div className={"mb-4"}>
                <div className={FIELD_LABEL_CLASS}>Contact</div>
                <input
                  className={READONLY_INPUT_CLASS}
                  value={customerName}
                  disabled
                />
              </div>
              <div className={"mb-4 grid grid-cols-[1fr_2.2fr] gap-4"}>
                <div>
                  <div className={FIELD_LABEL_CLASS}>Type</div>
                  <input
                    className={READONLY_INPUT_CLASS}
                    value={timerTypeLabel}
                    disabled
                  />
                </div>
                <div>
                  <div className={FIELD_LABEL_CLASS}>Task Name</div>
                  <input
                    className={READONLY_INPUT_CLASS}
                    value={taskName}
                    disabled
                  />
                </div>
              </div>
              <div className={"mb-4 grid grid-cols-[1fr_2.2fr] gap-4"}>
                <div className={"w-full"}>
                  <div className={FIELD_LABEL_CLASS}>Date</div>
                  <Controller
                    control={control}
                    name={"date"}
                    render={({ field: { onChange, value } }) => (
                      <CompactDatePicker
                        value={value}
                        format={DATE_FORMATS.dateOnly}
                        mountNodeId={"edit-time"}
                        onChange={(date) => onChange(transformDateToUTC(date))}
                      />
                    )}
                  />
                </div>
                <div className={"w-full font-medium"}>
                  <div className={FIELD_LABEL_CLASS}>Duration</div>
                  <InputTime
                    value={time}
                    onChange={setTime}
                    inputClassName={"h-8 text-sm font-medium"}
                  />
                </div>
              </div>
              <BillableSwitch
                value={isBillable}
                onChange={setIsBillable}
                className={"mb-4"}
              />
              <div className={"mb-4"}>
                <div className={FIELD_LABEL_CLASS}>Notes</div>
                <div>
                  <textarea
                    placeholder={"Add a note...(optional)"}
                    onChange={(e) => setNotes(e.target.value)}
                    className={
                      "input input-bordered min-h-[100px] w-full py-[8px] text-sm font-medium placeholder:text-gray-400"
                    }
                  />
                </div>
              </div>
              <div className={"flex justify-between"}>
                <Button
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  buttonType={"button"}
                  colorType={"error"}
                  label={"Delete"}
                  size={"custom"}
                  extraClasses={`normal-case ${ACTION_BUTTON_CLASS}`}
                />
                <div className={"flex space-x-[10px]"}>
                  <OutlineButton
                    colorType={"neutral"}
                    label={"Cancel"}
                    onClick={() => {
                      dispatch(
                        startTimer({
                          workId: timer?.workId || null,
                          customerId: timer?.customerId || "",
                          assignedUserId: timer?.assignedUserId || "",
                          jobTaskId: isWorkTaskTimer
                            ? timer?.jobTaskId
                            : undefined,
                          workTask: isWorkTaskTimer
                            ? timer?.workTask
                            : undefined,
                          work: timer?.work,
                          customerName: timer?.customerName,
                        }),
                      );
                      closeModal();
                    }}
                    size={"small"}
                    extraClasses={`normal-case ${ACTION_BUTTON_CLASS} border-[#D4D6D9] bg-white text-[#253747]`}
                  />
                  <Button
                    buttonType={"submit"}
                    label={"Save"}
                    size={"custom"}
                    extraClasses={`normal-case ${ACTION_BUTTON_CLASS}`}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      {isDeleteConfirmOpen && (
        <SettingsConfirmModal
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={handleDelete}
          icon={<LuTrash2 size={24} />}
          title={"Delete this time entry?"}
          description={"Do you want to permanently delete this time entry?"}
          confirmText={"Delete"}
        />
      )}
    </>
  );
};

export default AddTimer;
